<template>
  <div class="width-100-p">
    <div class="width-100-p">
      <div class="title-wrapper">
        <h1 class="title">
          {{ $t("AuthorizationPanel") }}
        </h1>
      </div>
    </div>
    <SimpleWarningDialog
      v-if="userDeleteDialog"
      :title="$t('warning')"
      :text="$t('deleteUserWarningText')"
      :secondaryBtn="true"
      @onPrimary="deleteUser"
      @onSecondary="userDeleteDialog = false"
    />
    <SimpleWarningDialog
      v-if="userChangeStatusActiveDialog"
      :title="$t('warning')"
      :text="$t('activeUserWarningText')"
      :secondaryBtn="true"
      @onPrimary="changeStatusActive"
      @onSecondary="userChangeStatusActiveDialog = false"
    />
      <SimpleWarningDialog
      v-if="userChangeStatusPassiveDialog"
      :title="$t('warning')"
      :text="$t('passiveUserWarningText')"
      :secondaryBtn="true"
      @onPrimary="changeStatusPassive"
      @onSecondary="userChangeStatusPassiveDialog = false"
    />
    <v-card>
      <v-tabs
        v-model="tab"
        background-color="primary"
        centered
        dark
        icons-and-text
      >
        <v-tabs-slider></v-tabs-slider>

        <v-tab href="#tab-1">
          {{ $t("users") }}
          <v-icon>mdi-face</v-icon>
        </v-tab>

        <v-tab href="#tab-2">
          {{ $t("profiles") }}
          <v-icon>mdi-face-profile</v-icon>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <div class="content-container">
            <div class="content-wrapper">
              <v-data-table
                :headers="headers"
                :items="items"
                :items-per-page="pageOptions.itemsPerPage"
                @update:items-per-page="onItemsPerPageChanged"
                :options.sync="pageOptions"
                :footer-props="footerProps"
                disable-sort
                :no-results-text="$t('no_result_found')"
                :no-data-text="$t('no_data_found')"
                @update:page="onPageChanged"
                :search="searchComputed"
                :custom-filter="customFilter"
              >
                <template v-slot:top>
                  <v-card class="d-flex flex-row-reverse" flat tile>
                    <v-btn
                      color="primary"
                      dark
                      class="mb-2"
                      @click="onBeforeAddUser"
                    >
                      {{ $t("addNewUser") }}
                    </v-btn>
                  </v-card>
                  <v-expand-transition>
                    <v-stepper
                      v-show="userForm.visible"
                      v-model="userForm.step"
                      alt-labels
                    >
                      <v-stepper-header>
                        <v-stepper-step step="1" :complete="userForm.step > 1">
                          {{ $t("userInformation") }}
                        </v-stepper-step>
                        <v-divider></v-divider>
                        <v-stepper-step step="2" :complete="userForm.step > 2">
                          {{ $t("review") }}
                        </v-stepper-step>
                      </v-stepper-header>

                      <v-stepper-content step="1">
                        <v-form ref="form" :lazy-validation="false">
                          <v-card>
                            <v-card-title>
                              <span class="headline">{{
                                userForm.type == 1
                                  ? $t("new") + " " + $t("userInformation")
                                  : $t("updateUserInformation")
                              }}</span>
                            </v-card-title>

                            <v-card-text>
                              <v-container>
                                <v-row>
                                  <v-col :cols="4">
                                    <v-text-field
                                      v-model="userForm.fullName"
                                      :label="$t('fullName')"
                                      :rules="[rules.fullNameRequired]"
                                      maxLength="40"
                                      @keypress="isLetter"
                                    ></v-text-field>
                                  </v-col>
                                  <v-col :cols="4">
                                    <v-text-field
                                      v-model="userForm.email"
                                      :rules="[
                                        rules.emailRequired,
                                        rules.emailValid,
                                        rules.emailValidLength,
                                        rules.emailExists
                                      ]"
                                      :label="$t('email')"
                                      maxLength="255"
                                      required
                                    ></v-text-field>
                                  </v-col>
                                  <v-col :cols="4">
                                    <v-text-field
                                      v-model="userForm.userName"
                                      :label="$t('username')"
                                      :rules="[
                                        rules.userNameRequired,
                                        rules.userNameExists
                                      ]"
                                      maxLength="40"
                                      @keypress="isLetter"
                                    ></v-text-field>
                                  </v-col>
                                  <v-col :cols="8">
                                    <v-select
                                      v-model="userForm.profile"
                                      :items="profileList"
                                      :rules="[rules.profileRequired]"
                                      :label="$t('profile')"
                                      item-text="text"
                                      item-value="name"
                                    ></v-select>
                                  </v-col>
                                  <v-col :cols="4">
                                    <v-combobox
                                      v-model="userForm.unitCode"
                                      :items="unitCodeTypePair"
                                      :rules="[rules.unitCodeRequired]"
                                      :label="$t('unit')"
                                      :search-input.sync="userForm.searchInput"
                                      item-text="unitType"
                                      item-value="unitCode"
                                      :maxlength="25"
                                      :counter="25"
                                      @change="onUnitCodeChange"
                                    ></v-combobox>
                                  </v-col>

                                  <v-col
                                    :cols="8"
                                    v-if="userForm.profile != null"
                                  >
                                    <v-list>
                                      <v-list-item-title>
                                        {{ $t("roleOfProfiles") + " :" }}
                                      </v-list-item-title>
                                      <v-list-item
                                        v-for="role in selectedProfilesRole"
                                        :key="role"
                                      >
                                        {{ "• " + role }}
                                      </v-list-item>
                                    </v-list>
                                  </v-col>
                                  <v-col
                                    :offset="poolOffset"
                                    :cols="4"
                                    v-if="userForm.unitCode != null"
                                  >
                                    <v-text-field
                                      v-model="unitCodePool"
                                      :label="$t('pool')"
                                      :disabled="true"
                                    ></v-text-field>
                                  </v-col>
                                </v-row>
                              </v-container>
                            </v-card-text>

                            <v-card-actions>
                              <v-spacer></v-spacer>
                              <v-btn
                                color="blue darken-1"
                                text
                                @click="closeDialog"
                              >
                                {{ $t("cancel") }}
                              </v-btn>
                              <v-btn
                                color="blue darken-1"
                                text
                                @click="prepareUserRequest"
                              >
                                {{ $t("continue") }}
                              </v-btn>
                            </v-card-actions>
                          </v-card>
                        </v-form>
                      </v-stepper-content>
                      <v-stepper-content step="2">
                        <v-card>
                          <v-card-title>
                            <span class="headline">{{ $t("review") }}</span>
                          </v-card-title>
                          <v-card-text>
                            <v-container>
                              <v-row>
                                <v-col :cols="4">
                                  {{ $t("fullName") + " : " }}
                                </v-col>
                                <v-col :cols="4">
                                  {{ userForm.fullName }}
                                </v-col>
                              </v-row>
                              <v-row v-if="userForm.type == 1">
                                <v-col :cols="4">
                                  {{ $t("email") + " : " }}
                                </v-col>
                                <v-col :cols="4">
                                  {{ userForm.email }}
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col :cols="4">
                                  {{ $t("username") + " : " }}
                                </v-col>
                                <v-col :cols="4">
                                  {{ userForm.userName }}
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col :cols="4">
                                  {{ $t("unit") + " : " }}
                                </v-col>
                                <v-col
                                  :cols="4"
                                  v-if="userForm.unitCode != null"
                                >
                                  {{ $t(userForm.unitCode.unitType) }}
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col :cols="4">
                                  {{ $t("profile") + " : " }}
                                </v-col>
                                <v-col :cols="4">
                                  {{
                                    profileResponse.find(
                                      (x) => x.name == userForm.profile
                                    ) != null
                                      ? $t(
                                          profileResponse.find(
                                            (x) => x.name == userForm.profile
                                          ).name
                                        )
                                      : ""
                                  }}
                                </v-col>
                              </v-row>
                            </v-container>
                          </v-card-text>

                          <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                              color="blue darken-1"
                              text
                              @click="stepBack(userForm)"
                            >
                              {{ $t("cancel") }}
                            </v-btn>
                            <v-btn color="blue darken-1" text @click="finalize">
                              {{
                                userForm.type == 1
                                  ? $t("addNewUser")
                                  : $t("updateUser")
                              }}
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-stepper-content>
                    </v-stepper>
                  </v-expand-transition>
                </template>
                <template v-slot:header>
                  <tr>
                    <td>
                      <v-text-field
                        v-model="searchFullName"
                        :label="$t('fullName')"
                        class="mx-4"
                        @blur="refreshTable"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model="searchUserName"
                        :label="$t('username')"
                        class="mx-4"
                        @blur="refreshTable"
                      ></v-text-field>
                    </td>
                    <td></td>
                  </tr>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                  <v-icon small class="mr-2" @click="editUser(item)">
                    mdi-pencil
                  </v-icon>
                  <v-icon small class="mr-2" @click="openUserStatusPopup(item)">
                     mdi-reload
                  </v-icon>
                </template>
              </v-data-table>
            </div>
          </div>
        </v-tab-item>
        <v-tab-item value="tab-2">
          <div class="content-container">
            <div class="content-wrapper">
              <v-data-table
                :headers="profileHeaders"
                :items="profileList"
                disable-sort
                :no-results-text="$t('no_result_found')"
                :no-data-text="$t('no_data_found')">
                <template v-slot:top>
                  <v-card class="d-flex flex-row-reverse" flat tile>
                    <v-btn
                      color="primary"
                      dark
                      class="mb-2 mr-2"
                      @click="onBeforeAddProfile"
                    >
                      {{ $t("addNewProfile") }}
                    </v-btn>
                  </v-card>
                  <v-expand-transition>
                    <v-stepper
                      v-show="profileForm.visible"
                      v-model="profileForm.step"
                      alt-labels
                    >
                      <v-stepper-header>
                        <v-stepper-step
                          step="1"
                          :complete="profileForm.step > 1"
                        >
                          {{ $t("profileInformation") }}
                        </v-stepper-step>
                        <v-divider></v-divider>
                        <v-stepper-step
                          step="2"
                          :complete="profileForm.step > 2"
                        >
                          {{ $t("assignRoles") }}
                        </v-stepper-step>
                        <v-divider></v-divider>
                        <v-stepper-step
                          step="3"
                          :complete="profileForm.step > 3"
                        >
                          {{ $t("review") }}
                        </v-stepper-step>
                      </v-stepper-header>

                      <v-stepper-content step="1">
                        <v-form ref="profileForm" :lazy-validation="false">
                          <v-card>
                            <v-card-title>
                              <span class="headline">{{
                                profileForm.type == 1
                                  ? $t("new") + " " + $t("profile")
                                  : $t("updateProfileInformation")
                              }}</span>
                            </v-card-title>

                            <v-card-text>
                              <v-container>
                                <v-row>
                                  <v-col :cols="4">
                                    <v-text-field
                                      v-model="profileForm.name"
                                      :label="$t('profile')"
                                      :rules="[rules.profileRequired]"
                                      :maxlength="40"
                                      :counter="40"
                                      @keypress="isLetter"
                                    ></v-text-field>
                                  </v-col>
                                  <v-col :cols="8">
                                    <v-text-field
                                      v-model="profileForm.description"
                                      :label="$t('description')"
                                      :rules="[rules.descriptionRequired]"
                                      :maxlength="250"
                                      :counter="250"
                                      @keypress="isLetter"
                                    ></v-text-field>
                                  </v-col>
                                </v-row>
                              </v-container>
                            </v-card-text>

                            <v-card-actions>
                              <v-spacer></v-spacer>
                              <v-btn
                                color="blue darken-1"
                                text
                                @click="closeDialog"
                              >
                                {{ $t("cancel") }}
                              </v-btn>
                              <v-btn
                                color="blue darken-1"
                                text
                                @click="prepareProfileRequest"
                              >
                                {{ $t("continue") }}
                              </v-btn>
                            </v-card-actions>
                          </v-card>
                        </v-form>
                      </v-stepper-content>
                      <v-stepper-content step="2">
                        <v-form ref="assignRolesForm" :lazy-validation="false">
                          <v-card>
                            <v-card-title>
                              <span class="headline">{{
                                $t("assignRoles")
                              }}</span>
                            </v-card-title>

                            <v-card-text>
                              <v-container>
                                <v-row>
                                  <v-col :cols="6">
                                    <v-select
                                      v-model="profileForm.roles"
                                      :items="roleList"
                                      :label="$t('select')"
                                      :menu-props="{ maxHeight: '400' }"
                                      multiple
                                      :hint="$t('canSelectMultipleRoles')"
                                      chips
                                      persistent-hint
                                      item-text="text"
                                      item-value="value"
                                    ></v-select>
                                  </v-col>
                                </v-row>
                              </v-container>
                            </v-card-text>

                            <v-card-actions>
                              <v-spacer></v-spacer>
                              <v-btn
                                color="blue darken-1"
                                text
                                @click="stepBack(profileForm)"
                              >
                                {{ $t("cancel") }}
                              </v-btn>
                              <v-btn
                                color="blue darken-1"
                                text
                                @click="prepareUpdateRolesRequest"
                              >
                                {{ $t("continue") }}
                              </v-btn>
                            </v-card-actions>
                          </v-card>
                        </v-form>
                      </v-stepper-content>
                      <v-stepper-content step="3">
                        <v-card>
                          <v-card-title>
                            <span class="headline">{{ $t("review") }}</span>
                          </v-card-title>
                          <v-card-text>
                            <v-container>
                              <v-row>
                                <v-col :cols="4">
                                  {{ $t("profile") + " : " }}
                                </v-col>
                                <v-col :cols="8">
                                  {{ profileForm.name }}
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col :cols="4">
                                  {{ $t("description") + " : " }}
                                </v-col>
                                <v-col :cols="8">
                                  {{ profileForm.description }}
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col :cols="4">
                                  {{ $t("assignedRoles") + " : " }}
                                </v-col>
                                <v-col :cols="8">
                                  <v-row
                                    v-for="role in profileForm.roles"
                                    :key="role"
                                  >
                                    <v-col>
                                      {{
                                        $t(
                                          roleList.find((x) => x.value == role)
                                            .text
                                        )
                                      }}
                                    </v-col>
                                  </v-row>
                                </v-col>
                              </v-row>
                            </v-container>
                          </v-card-text>

                          <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                              color="blue darken-1"
                              text
                              @click="stepBack(profileForm)"
                            >
                              {{ $t("cancel") }}
                            </v-btn>
                            <v-btn
                              color="blue darken-1"
                              text
                              @click="finalizeProfile"
                            >
                              {{
                                profileForm.type == 1
                                  ? $t("addNewProfile")
                                  : $t("updateProfile")
                              }}
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-stepper-content>
                    </v-stepper>
                  </v-expand-transition>
                </template>
                <template v-slot:[`item.value`]="{ item }">
                  {{ $t(item.value) }}
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                  <v-icon small class="mr-2" @click="editProfile(item)">
                    mdi-pencil
                  </v-icon>
                  <v-icon
                    small
                    class="mr-2"
                    @click="openDeleteProfilePopup(item)"
                  >
                    mdi-delete
                  </v-icon>
                </template>
              </v-data-table>
            </div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>
<script>
import AuthorizationPanel from "./mixin/AuthorizationPanel";


export default {
  mixins: [AuthorizationPanel]
};
</script>
<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// src/pages/authorization/AuthorizationPanel.vue